<template>
    <div>
        <DataTable
            :perPage="itemsPerPage"
            :key="tableKey"
            :columns="tableColumns"
            :items="paginatedItems"
            :currentPage="currentPage"
            :totalPages="totalPages"
            :selectedItems="selectedItems"
            :enableDragDrop="enableDragDrop"
            @drag-end="handleDragEnd"
            :enableMultiSelect="true"
            @page-changed="handlePageChange"
            @item-selected="toggleItemSelection"
            @select-all-toggled="toggleSelectAll"
            @sort="handleSort"
            @per-page-changed="handlePerPageChange"
            :sortBy="sortBy"
            :sortOrder="sortOrder"
        >
            <!-- Toolbar -->
            <template #toolbar>
                <!-- Category Filter Breadcrumb -->
                <div v-if="categoryFilter" class="ppv2-flex ppv2-items-center ppv2-gap-2 ppv2-mb-4">
                    <span
                        class="ppv2-text-[#7D2DBD] ppv2-cursor-pointer poppinsSemiBold ppv2-text-2xl hover:ppv2-underline"
                        @click="clearFilter"
                    >
                        {{ $t('food.carte.title') }}
                    </span>
                    <ChevronRight :size="20" class="ppv2-text-gray-400" />
                    <span class="poppinsSemiBold ppv2-text-2xl ppv2-text-black">
                        {{ categoryFilter.name }}
                    </span>
                    <button
                        @click="clearFilter"
                        class="ppv2-ml-2 ppv2-p-1 ppv2-rounded-full ppv2-bg-gray-200 hover:ppv2-bg-gray-300 ppv2-transition-colors"
                        :title="$t('food.carte.clear_filter')"
                    >
                        <X :size="16" class="ppv2-text-gray-600" />
                    </button>
                </div>

                <div class="ppv2-flex sm:ppv2-flex-row ppv2-flex-col ppv2-gap-2 ppv2-justify-between ppv2-items-center ppv2-mb-6">
                    <div class="ppv2-flex ppv2-flex-wrap ppv2-gap-3">
                        <!-- Search Input -->
                        <div class="ppv2-relative">
                            <Search
                                v-model="searchQuery"
                                :emitGlobal="false"
                                @search-updated="handleSearchUpdate"
                                keep-open-on-search
                            />
                        </div>

                        <FilterByType :selected="selectedType" :options="typeOptions" @filter-selected="selectType" />

                        <!-- Export Button -->
                        <ExportSelect
                            :options="[
                                { label: 'CSV', value: 'csv' },
                                { label: 'Excel', value: 'excel' },
                            ]"
                            :disabled="isExporting"
                            :hide-label-on-mobile="true"
                            @export-selected="exportData"
                        />

                        <!-- Filter Button -->
                        <button
                            @click="openFilterModal"
                            class="filterButton ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-2 ppv2-rounded-xl ppv2-border ppv2-border-solid ppv2-border-black ppv2-border-opacity-20 ppv2-text-black hover:ppv2-bg-opacity-80 ppv2-transition-all"
                        >
                            <LayoutGrid :size="17" />
                            <span class="poppinsLight ppv2-hidden md:ppv2-inline" :style="{ fontSize: normalizeFontSize(15) }">
                                {{ $t('products.actions.filter') }}
                            </span>
                            <span
                                v-if="activeFiltersCount > 0"
                                class="ppv2-flex ppv2-items-center ppv2-justify-center ppv2-bg-[#7D2DBD] ppv2-text-white ppv2-text-[10px] ppv2-w-5 ppv2-h-5 ppv2-rounded-full poppinsSemiBold"
                            >
                                {{ activeFiltersCount }}
                            </span>
                        </button>

                        <AdvancedFilterModal
                            v-model="showFilterModal"
                            :initialFilters="advancedFilters"
                            :availableFilters="['categories', 'status', 'tags', 'price_range']"
                            :categories="filterData.categories"
                            :tags="filterData.tags"
                            @apply="handleAdvancedFilters"
                        />
                        <div class="ppv2-relative md:ppv2-hidden ppv2-block" ref="addDropdown">
                            <BaseSplitButton
                                :label="$t('common.add')"
                                :icon="Plus"
                                :showDropdown="showAddDropdown"
                                @click="showAddDropdown = !showAddDropdown"
                                @toggle="showAddDropdown = !showAddDropdown"
                            />

                            <div
                                v-if="showAddDropdown"
                                class="menuDropdown ppv2-absolute ppv2-right-0 ppv2-top-full ppv2-mt-2 ppv2-w-48 ppv2-bg-white ppv2-rounded-xl ppv2-shadow-xl ppv2-z-50 ppv2-border ppv2-border-gray-100 ppv2-overflow-hidden"
                            >
                                <button
                                    @click="openCreateTab()"
                                    class="ppv2-w-full ppv2-text-left ppv2-px-4 ppv2-py-3 hover:ppv2-bg-gray-50 ppv2-transition-colors ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-text-gray-700"
                                >
                                    <Plus :size="16" class="ppv2-text-gray-400" />
                                    <span class="poppinsRegular ppv2-text-sm">{{ $t('food.carte.add_product') }}</span>
                                </button>
                                <button
                                    @click="openCreatePackTab()"
                                    class="ppv2-w-full ppv2-text-left ppv2-px-4 ppv2-py-3 hover:ppv2-bg-gray-50 ppv2-transition-colors ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-text-gray-700 ppv2-border-t ppv2-border-gray-50"
                                >
                                    <Plus :size="16" class="ppv2-text-gray-400" />
                                    <span class="poppinsRegular ppv2-text-sm">{{ $t('food.carte.add_pack') }}</span>
                                </button>
                            </div>
                        </div>
                    </div>

                    <div class="ppv2-relative ppv2-hidden md:ppv2-block" ref="addDropdown">
                        <BaseSplitButton
                            :label="$t('common.add')"
                            :icon="Plus"
                            :showDropdown="showAddDropdown"
                            @click="showAddDropdown = !showAddDropdown"
                            @toggle="showAddDropdown = !showAddDropdown"
                        />

                        <div
                            v-if="showAddDropdown"
                            class="menuDropdown ppv2-absolute ppv2-right-0 ppv2-top-full ppv2-mt-2 ppv2-w-48 ppv2-bg-white ppv2-rounded-xl ppv2-shadow-xl ppv2-z-50 ppv2-border ppv2-border-gray-100 ppv2-overflow-hidden"
                        >
                            <button
                                @click="openCreateTab()"
                                class="ppv2-w-full ppv2-text-left ppv2-px-4 ppv2-py-3 hover:ppv2-bg-gray-50 ppv2-transition-colors ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-text-gray-700"
                            >
                                <Plus :size="16" class="ppv2-text-gray-400" />
                                <span class="poppinsRegular ppv2-text-sm">{{ $t('food.carte.add_product') }}</span>
                            </button>
                            <button
                                @click="openCreatePackTab()"
                                class="ppv2-w-full ppv2-text-left ppv2-px-4 ppv2-py-3 hover:ppv2-bg-gray-50 ppv2-transition-colors ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-text-gray-700 ppv2-border-t ppv2-border-gray-50"
                            >
                                <Plus :size="16" class="ppv2-text-gray-400" />
                                <span class="poppinsRegular ppv2-text-sm">{{ $t('food.carte.add_pack') }}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </template>

            <!-- Column: Name -->
            <template #column-name="{ item }">
                <a
                    :href="getEditUrl(item)"
                    @click.prevent="openEditTab(item)"
                    class="ppv2-text-black hover:ppv2-text-[#7D2DBD] hover:ppv2-underline ppv2-transition-colors poppinsRegular"
                    :style="{ fontSize: normalizeFontSize(14) }"
                    :title="$t('experiences.actions.edit')"
                >
                    {{ item.name }}
                </a>
            </template>

            <!-- Column: Price -->
            <template #column-price="{ item }">
                <span class="ppv2-text-black poppinsRegular"> {{ formatPrice(item.price) }} {{ $symbolCurrency }} </span>
            </template>

            <!-- Column: Category -->
            <template #column-category="{ item }">
                <div
                    class="ppv2-flex ppv2-items-center ppv2-justify-center ppv2-gap-2 ppv2-cursor-pointer ppv2-px-3 ppv2-py-1.5 ppv2-rounded-lg ppv2-transition-all hover:ppv2-bg-gray-100"
                    :style="{ backgroundColor: item.category_color, color: 'black', width: 'max-content' }"
                    @click.stop="toggleCategoryDropdown(item.id)"
                    :ref="'categoryDropdown-' + item.id"
                >
                    <span>
                        {{ item.category_name || $t('food.carte.select') }}
                    </span>
                    <ChevronDown class="ppv2-w-4 ppv2-h-4" />
                </div>

                <!-- Category Dropdown Menu -->
                <div
                    v-if="activeCategoryDropdown === item.id"
                    class="menuDropdown ppv2-fixed ppv2-overflow-hidden ppv2-z-[100] ppv2-shadow-xl ppv2-border ppv2-border-gray-100"
                    :style="categoryDropdownPosition"
                    :ref="'categoryMenu-' + item.id"
                >
                    <div class="ppv2-max-h-[200px] ppv2-overflow-y-auto">
                        <button
                            v-for="cat in categories"
                            :key="cat.id"
                            @click="changeCategory(item, cat)"
                            class="dropdownItem ppv2-w-full ppv2-text-left ppv2-px-4 ppv2-py-2 ppv2-transition-colors ppv2-flex ppv2-items-center ppv2-gap-3"
                            :class="{ 'ppv2-bg-gray-50': item.category_id === cat.id }"
                        >
                            <span :class="{ 'ppv2-pl-4': false }">
                                <span v-if="cat.depth" class="ppv2-text-slate-400 ppv2-mr-1">{{ '- '.repeat(cat.depth) }}</span>
                                {{ cat.original_name || cat.name }}
                            </span>
                        </button>
                    </div>
                    <div class="ppv2-sticky ppv2-bottom-0 ppv2-bg-white ppv2-border-gray-100">
                        <button
                            @click="openCreateCategory"
                            class="dropdownItem ppv2-w-full ppv2-text-left ppv2-px-4 ppv2-py-2 ppv2-transition-colors ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-text-slate-600"
                        >
                            <Plus :size="14" />
                            <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                                {{ $t('food.carte.create_category') }}
                            </span>
                        </button>
                    </div>
                </div>
            </template>

            <!-- Column: Tags -->
            <template #column-tags="{ item }">
                <div class="ppv2-flex ppv2-flex-wrap ppv2-gap-1">
                    <span v-for="tag in item.tags || []" :key="tag" class="ppv2-underline ppv2-text-black"> #{{ tag }} </span>
                </div>
            </template>

            <!-- Column: Status -->
            <template #column-status="{ item }">
                <button
                    @click="toggleStatus(item)"
                    class="ppv2-min-w-[125px] ppv2-inline-flex ppv2-items-center ppv2-gap-2 ppv2-px-3 ppv2-py-2 ppv2-rounded-xl ppv2-border ppv2-border-solid ppv2-transition-all"
                    :class="
                        item.status === 'actif'
                            ? 'ppv2-border-[#206A64] ppv2-bg-[#06B217] ppv2-bg-opacity-20'
                            : 'ppv2-border-[#4E4E4E] ppv2-bg-[#474747] ppv2-bg-opacity-20'
                    "
                >
                    <div
                        class="ppv2-w-9 ppv2-h-5 ppv2-rounded-full ppv2-flex ppv2-items-center ppv2-transition-all"
                        :class="
                            item.status === 'actif'
                                ? 'ppv2-bg-[#206A64] ppv2-justify-end'
                                : 'ppv2-bg-[#4E4E4E] ppv2-justify-start'
                        "
                    >
                        <div class="ppv2-w-3.5 ppv2-h-3.5 ppv2-bg-white ppv2-rounded-full ppv2-mx-0.5"></div>
                    </div>
                    <span class="ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                        {{ item.status === 'actif' ? $t('food.status.active') : $t('food.status.inactive') }}
                    </span>
                </button>
            </template>

            <!-- Column: Stock -->
            <template #column-stock="{ item }">
                <span
                    v-if="!item.manage_stock"
                    class="ppv2-underline ppv2-text-black"
                    :style="{ fontSize: normalizeFontSize(14) }"
                >
                    &infin;
                </span>
                <span
                    v-else
                    class="ppv2-text-center"
                    :class="item.stock < 5 ? 'poppinsSemiBold ppv2-text-[#911414]' : 'poppinsRegular ppv2-text-black'"
                    :style="{ fontSize: normalizeFontSize(14) }"
                >
                    {{ item.stock }}
                    <span v-if="item.stock < 5">
                        <TriangleAlert :size="18" color="#911414" />
                    </span>
                </span>
            </template>

            <!-- Column: Type -->
            <template #column-type="{ item }">
                <span
                    class="ppv2-px-2 ppv2-py-1 ppv2-rounded-lg ppv2-text-xs ppv2-uppercase poppinsSemiBold"
                    :class="item.type === 'pack' ? 'ppv2-bg-blue-100 ppv2-text-blue-600' : 'ppv2-bg-[#ead3ff] ppv2-text-gray-600'"
                >
                    {{ item.type === 'pack' ? 'Formule' : 'Produit' }}
                </span>
            </template>

            <!-- Column: Actions -->
            <template #column-actions="{ item }">
                <div class="ppv2-flex ppv2-items-center ppv2-justify-center ppv2-gap-3">
                    <button
                        @click="openEditTab(item)"
                        class="ppv2-p-1 ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                        :title="$t('common.edit')"
                    >
                        <SquarePen :size="18" />
                    </button>
                    <button
                        @click="duplicateItem(item)"
                        class="ppv2-p-1 ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                        :title="$t('common.duplicate')"
                    >
                        <CopyPlus :size="18" />
                    </button>
                    <button
                        class="ppv2-p-1 ppv2-text-[#FF9C9C] hover:ppv2-bg-[#FF9C9C] hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                        :title="$t('common.delete')"
                        @click="deleteSingleItem(item)"
                    >
                        <Trash2 :size="18" />
                    </button>
                </div>
            </template>

            <!-- Bulk Actions Slot -->
            <template #bulk-actions="{ selectedCount }">
                <span class="poppinsRegular textGray" :style="{ fontSize: normalizeFontSize(15) }">
                    {{ $t('food.messages.selected_items', { count: selectedCount }) }}
                </span>
                <BaseButton
                    variant="outline"
                    @click="openBulkCategoryModal"
                    customClass="!ppv2-h-[30px] !ppv2-flex !ppv2-items-center !ppv2-gap-2"
                >
                    <LayoutGrid :size="18" />
                    <span :style="{ fontSize: normalizeFontSize(14) }">{{ $t('food.carte.bulk.edit_category') }}</span>
                </BaseButton>
                <BaseButton
                    variant="outline"
                    @click="openBulkTagModal"
                    customClass="!ppv2-h-[30px] !ppv2-flex !ppv2-items-center !ppv2-gap-2"
                >
                    <Hash :size="18" />
                    <span :style="{ fontSize: normalizeFontSize(14) }">{{ $t('food.carte.bulk.edit_tag') }}</span>
                </BaseButton>
                <BaseButton
                    variant="outline"
                    @click="openBulkDiscountModal"
                    customClass="!ppv2-h-[30px] !ppv2-flex !ppv2-items-center !ppv2-gap-2"
                >
                    <Percent :size="18" />
                    <span :style="{ fontSize: normalizeFontSize(14) }">{{ $t('food.carte.bulk.apply_discount') }}</span>
                </BaseButton>
                <BaseButton
                    variant="dangerOutline"
                    @click="deleteSelectedItems"
                    customClass="!ppv2-h-[30px] !ppv2-flex !ppv2-items-center !ppv2-gap-2"
                >
                    <Trash2 :size="18" />
                    <span :style="{ fontSize: normalizeFontSize(14) }">{{ $t('common.delete') }}</span>
                </BaseButton>
            </template>
        </DataTable>

        <!-- Category Creation Modal -->
        <CategoryModal
            v-model="isCreateCategoryModalOpen"
            :isEditMode="false"
            :isSaving="isSavingCategory"
            :categoryForm="categoryForm"
            :parentCategories="categories"
            :availableColors="availableColors"
            :tagsOptions="tagsOptions"
            @save="saveCategory"
        />

        <!-- Bulk Category Modal -->
        <CustomDialog
            v-model="showBulkCategoryModal"
            :defaultTitle="$t('food.carte.bulk.edit_category')"
            height="300px"
            heightClass="ppv2-max-h-[60vh]"
        >
            <template #content>
                <p class="ppv2-text-sm ppv2-text-gray-600 ppv2-mb-4">
                    {{ $t('food.messages.confirm_bulk_category', { count: selectedItems.length }) }}
                </p>
                <BaseSelect
                    v-model="bulkCategoryId"
                    :label="$t('food.productForm.categoryLabel')"
                    class="ppv2-mt-3"
                    :placeholder="$t('food.productForm.categoryPlaceholder')"
                    :options="categories"
                    optionLabel="name"
                    optionValue="id"
                    dropdownPlacement="top"
                    fixedDropdown
                />
            </template>
            <template #actions>
                <BaseButton variant="secondary" @click="showBulkCategoryModal = false">{{ $t('common.cancel') }} </BaseButton>
                <BaseButton variant="primary" @click="applyBulkCategory" :disabled="!bulkCategoryId">{{
                    $t('common.apply')
                }}</BaseButton>
            </template>
        </CustomDialog>

        <!-- Bulk Tag Modal -->
        <CustomDialog v-model="showBulkTagModal" :defaultTitle="$t('food.carte.bulk.edit_tag')" heightClass="ppv2-max-h-[60vh]">
            <template #content>
                <p class="ppv2-text-sm ppv2-text-gray-600 ppv2-mb-4">
                    {{ $t('food.messages.confirm_bulk_tags', { count: selectedItems.length }) }}
                </p>
                <BaseRadioGroup v-model="bulkTagMode" :options="bulkTagModeOptions" class="ppv2-mb-4 ppv2-mt-4" :inline="true" />
                <BaseSelect
                    v-model="bulkTags"
                    :label="$t('food.carte.bulk.tags_label')"
                    :placeholder="$t('food.carte.bulk.tags_placeholder')"
                    :options="tagsOptions"
                    :reduce="(opt) => opt"
                    optionLabel="label"
                    multiple
                    taggable
                    dropdownPlacement="top"
                    classAdded="borderInput"
                    fixedDropdown
                />
            </template>
            <template #actions>
                <BaseButton variant="secondary" @click="showBulkTagModal = false">{{ $t('common.cancel') }}</BaseButton>
                <BaseButton variant="primary" @click="applyBulkTags">{{ $t('common.apply') }}</BaseButton>
            </template>
        </CustomDialog>

        <!-- Bulk Discount Modal -->
        <CustomDialog
            v-model="showBulkDiscountModal"
            :defaultTitle="$t('food.carte.bulk.apply_discount')"
            heightClass="ppv2-max-h-[50vh]"
        >
            <template #content>
                <p class="ppv2-text-sm ppv2-text-gray-600 ppv2-mb-4">
                    {{ $t('food.messages.confirm_bulk_discount', { count: selectedItems.length }) }}
                </p>
                <BaseRadioGroup
                    v-model="bulkDiscountType"
                    :label="$t('food.carte.bulk.discount_type')"
                    :options="bulkDiscountTypeOptions"
                    class="ppv2-mb-4 ppv2-mt-4"
                    :inline="true"
                />
                <BaseInput
                    v-model="bulkDiscountValue"
                    :label="
                        bulkDiscountType === 'percentage'
                            ? $t('food.carte.bulk.discount_percent')
                            : $t('food.carte.bulk.discount_amount', { symbol: $symbolCurrency })
                    "
                    :placeholder="bulkDiscountType === 'percentage' ? '10' : '5.00'"
                    type="number"
                    step="any"
                />
            </template>
            <template #actions>
                <BaseButton variant="secondary" @click="showBulkDiscountModal = false">{{ $t('common.cancel') }} </BaseButton>
                <BaseButton variant="primary" @click="applyBulkDiscount" :disabled="!bulkDiscountValue">{{
                    $t('common.apply')
                }}</BaseButton>
            </template>
        </CustomDialog>

        <!-- Delete Confirmation Popup -->
        <CustomDialog v-model="showDeletePopup" :defaultTitle="$t('common.delete')" customClassTitle="poppinsSemiBold" noScroll>
            <template #content>
                <p class="ppv2-text-center ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(15) }">
                    {{ deletePopupDescription }}
                </p>
            </template>
            <template #actions>
                <BaseButton
                    type="button"
                    variant="outline"
                    @click="showDeletePopup = false"
                    :customClass="`!ppv2-h-[45px] !ppv2-text-base !ppv2-min-w-[170px]`"
                >
                    {{ $t('common.cancel') }}
                </BaseButton>
                <BaseButton
                    type="submit"
                    variant="danger"
                    @click="confirmDelete"
                    :customClass="`!ppv2-h-[45px] !ppv2-text-base !ppv2-min-w-[170px]`"
                >
                    {{ $t('common.delete') }}
                </BaseButton>
            </template>
        </CustomDialog>

        <!-- Duplicate Confirmation Popup -->
        <CustomDialog
            v-model="showDuplicatePopup"
            defaultTitle="Dupliquer"
            height="250px"
            minHeight="250px"
            customClassTitle="poppinsSemiBold"
        >
            <template #content>
                <p class="ppv2-text-center ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(15) }">
                    {{ duplicatePopupDescription }}
                </p>
            </template>
            <template #actions>
                <BaseButton
                    type="button"
                    variant="outline"
                    @click="showDuplicatePopup = false"
                    :customClass="`!ppv2-h-[45px] !ppv2-text-base !ppv2-min-w-[170px]`"
                >
                    {{ $t('common.cancel') }}
                </BaseButton>
                <BaseButton
                    type="submit"
                    variant="primary"
                    @click="confirmDuplicate"
                    :customClass="`!ppv2-h-[45px] !ppv2-text-base !ppv2-min-w-[170px]`"
                >
                    {{ $t('common.duplicate') }}
                </BaseButton>
            </template>
        </CustomDialog>
    </div>
</template>

<script>
import {
    LayoutGrid,
    ChevronDown,
    ChevronRight,
    X,
    Plus,
    SquarePen,
    CopyPlus,
    Trash2,
    Hash,
    Percent,
    TriangleAlert,
    ChevronLeft,
} from 'lucide-vue'
import normalizeSize from '../../../utils/normalizeSize'
import Search from '../Experience/components/Search.vue'
import FilterByType from '../../Shared/FilterByType.vue'
import BaseButton from '../../Shared/BaseButton.vue'
import BaseSplitButton from '../../Shared/BaseSplitButton.vue'
import BaseSelect from '../../Shared/BaseSelect.vue'
import BaseInput from '../../Shared/BaseInput.vue'
import BaseRadioGroup from '../../Shared/BaseRadioGroup.vue'
import CustomDialog from '../../Shared/CustomDialog.vue'
import DataTable from '../../Shared/DataTable.vue'
import ExportSelect from '../../Shared/ExportSelect.vue'
import AdvancedFilterModal from '../../Shared/AdvancedFilterModal.vue'
import CategoryModal from './CategoryModal.vue'
import normalizeFontSize from '../../../utils/normalizeFontSize'
import { getPerPage, setPerPage } from '../../../utils/perPage'

export default {
    name: 'BoFoodV3Carte',
    components: {
        LayoutGrid,
        ChevronDown,
        ChevronRight,
        X,
        SquarePen,
        Plus,
        CopyPlus,
        Trash2,
        Hash,
        Percent,
        Search,
        BaseButton,
        BaseSplitButton,
        BaseSelect,
        BaseInput,
        BaseRadioGroup,
        CustomDialog,
        DataTable,
        ExportSelect,
        AdvancedFilterModal,
        CategoryModal,
        FilterByType,
    },
    props: {
        categoryFilter: {
            type: Object,
            default: null, // { id, slug, name }
        },
        establishment: {
            type: Object,
            default: () => ({}),
        },
    },
    // ... data, computed, methods ...
    data() {
        return {
            Plus,
            loading: false,
            searchQuery: '',
            selectedType: 'tout',
            typeOptions: [
                { label: this.$t('common.all'), value: 'tout' },
                { label: this.$t('food.navigation.products'), value: 'food' },
                { label: this.$t('food.navigation.packs'), value: 'pack' },
            ],
            currentPage: 1,
            itemsPerPage: getPerPage('food_carte_items_per_page', 10),
            sortBy: 'order',
            sortOrder: 'asc',
            isExportDropdownOpen: false,
            isFilterDropdownOpen: false,
            showAddDropdown: false,
            showFilterModal: false,
            activeCategoryDropdown: null,
            selectedItems: [],
            showDeletePopup: false,
            deletePopupDescription: '',
            deleteAction: null,
            itemToDelete: null,
            showDuplicatePopup: false,
            duplicatePopupDescription: '',
            itemToDuplicate: null,
            items: [],
            totalPages: 1,
            tableKey: 0,
            isSavingPositions: false,
            categories: [], // This is for the item dropdown, might want to reuse
            isExporting: false,
            advancedFilters: {
                category_ids: [],
                kitchen_ids: [],
                status: 'all',
                tag_ids: [],
                price_min: null,
                price_max: null,
            },
            filterData: {
                categories: [],
                kitchens: [],
                tags: [],
            },
            // Category Modal State
            isCreateCategoryModalOpen: false,
            isSavingCategory: false,
            categoryForm: {
                name: '',
                parent_id: null,
                slug: '',
                tags: [],
                color: '#FFD7D7',
                active: true,
                image: null,
            },
            tagsOptions: [],
            availableColors: [
                '#FFD7D7',
                '#FFF4D7',
                '#D7F2FF',
                '#D7FFDC',
                '#FFEBD7',
                '#FAD7FF',
                '#8B9EFF',
                '#FFAB91',
                '#FFF59D',
                '#CE93D8',
                '#B39DDB',
            ],
            // Bulk Action Modals
            showBulkCategoryModal: false,
            bulkCategoryId: null,
            showBulkTagModal: false,
            bulkTagMode: 'add',
            bulkTags: [],
            showBulkDiscountModal: false,
            bulkDiscountType: 'percentage',
            bulkDiscountValue: null,
            categoryDropdownPosition: { top: '0px', left: '0px', width: 'auto' },
        }
    },
    computed: {
        enableDragDrop() {
            if (this.isSavingPositions) return false
            if (this.searchQuery && this.searchQuery.trim() !== '') return false
            if (this.sortBy !== 'order' || this.sortOrder !== 'asc') return false
            return true
        },
        tableColumns() {
            return [
                { key: 'name', label: this.$t('food.columns.name'), sortable: true, type: 'text' },
                { key: 'price', label: this.$t('food.columns.price'), sortable: true, type: 'number' },
                { key: 'category', label: this.$t('food.columns.category'), sortable: true, type: 'text' },
                { key: 'stock', label: this.$t('food.columns.stock'), sortable: true, type: 'number' },
                { key: 'type', label: 'Type', sortable: false, type: 'badge' },
                { key: 'tags', label: 'Tags', sortable: false, type: 'text' },
                { key: 'status', label: this.$t('food.columns.status'), sortable: true, type: 'badge' },
                { key: 'actions', label: this.$t('food.columns.actions'), sortable: false, type: 'actions' },
            ]
        },
        paginatedItems() {
            return this.items
        },
        activeFiltersCount() {
            let count = 0
            if (this.advancedFilters.status !== 'all') count++
            if (this.advancedFilters.tag_ids?.length > 0) count++
            if (this.advancedFilters.price_min !== null && this.advancedFilters.price_min !== '') count++
            if (this.advancedFilters.price_max !== null && this.advancedFilters.price_max !== '') count++
            if (this.advancedFilters.category_ids?.length > 0) count++
            return count
        },
        bulkTagModeOptions() {
            return [
                { value: 'add', label: this.$t('common.add') },
                { value: 'remove', label: this.$t('common.remove') },
                { value: 'replace', label: this.$t('common.replace') },
            ]
        },
        bulkDiscountTypeOptions() {
            return [
                { value: 'percentage', label: this.$t('food.carte.bulk.percentage') },
                { value: 'fixed', label: this.$t('food.carte.bulk.fixed_amount', { symbol: this.$symbolCurrency }) },
            ]
        },
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        formatPrice(price) {
            return Number(price || 0)
                .toFixed(2)
                .replace('.', ',')
        },
        handlePageChange(page) {
            this.currentPage = page
            this.fetchData()
        },
        handleSearchUpdate(val) {
            this.searchQuery = val
            this.currentPage = 1
            this.fetchData()
        },
        handlePerPageChange(val) {
            this.itemsPerPage = Number(val)
            setPerPage('food_carte_items_per_page', this.itemsPerPage)
            this.currentPage = 1
            this.fetchData()
        },
        toggleItemSelection(itemId) {
            const index = this.selectedItems.indexOf(itemId)
            if (index > -1) {
                this.selectedItems.splice(index, 1)
            } else {
                this.selectedItems.push(itemId)
            }
        },
        toggleSelectAll() {
            if (this.selectedItems.length === this.items.length) {
                this.selectedItems = []
            } else {
                this.selectedItems = this.items.map((item) => item.id)
            }
        },
        async toggleStatus(item) {
            const previousStatus = item.status
            const newStatus = previousStatus === 'actif' ? 'inactif' : 'actif'
            item.status = newStatus

            try {
                const response = await axios.patch('/back-office/v3/food/update-status', {
                    id: item.type === 'pack' ? item._numeric_id : item._uuid,
                    active: newStatus === 'actif',
                    type: item.type === 'pack' ? 'pack' : 'food',
                })

                if (!response.data.success) {
                    item.status = previousStatus
                    alert(this.$t('food.messages.status_update_error'))
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                item.status = previousStatus
            }
        },
        toggleCategoryDropdown(itemId) {
            if (this.activeCategoryDropdown === itemId) {
                this.activeCategoryDropdown = null
            } else {
                this.activeCategoryDropdown = itemId
                this.$nextTick(() => {
                    this.calculateCategoryDropdownPosition(itemId)
                })
            }
        },
        calculateCategoryDropdownPosition(itemId) {
            const button = this.$refs['categoryDropdown-' + itemId]
            const btnEl = Array.isArray(button) ? button[0] : button
            if (!btnEl) return

            const rect = btnEl.getBoundingClientRect()
            const DROPDOWN_WIDTH = 192 // w-48 = 12rem = 192px
            const MARGIN = 8 // safe edge margin in px
            const viewportWidth = window.innerWidth

            // Prefer aligning to the left edge of the button, but clamp so it
            // never overflows right or left edge of the viewport.
            let left = rect.left
            if (left + DROPDOWN_WIDTH > viewportWidth - MARGIN) {
                left = viewportWidth - DROPDOWN_WIDTH - MARGIN
            }
            if (left < MARGIN) {
                left = MARGIN
            }

            this.categoryDropdownPosition = {
                top: `${rect.bottom + 4}px`,
                left: `${left}px`,
                width: `${Math.min(DROPDOWN_WIDTH, viewportWidth - MARGIN * 2)}px`,
            }
        },
        updateCategoryDropdownPosition() {
            if (this.activeCategoryDropdown !== null) {
                this.calculateCategoryDropdownPosition(this.activeCategoryDropdown)
            }
        },
        async changeCategory(item, category) {
            const previousCategoryId = item.category_id
            const previousCategoryName = item.category_name
            const previousCategoryColor = item.category_color

            // Optimistic UI update
            item.category_id = category.id
            item.category_name = category.name
            item.category_color = category.color
            this.activeCategoryDropdown = null

            try {
                const endpoint =
                    item.type === 'pack' ? '/back-office/v3/food/packs/update-category' : '/back-office/v3/food/update-category'

                await axios.patch(endpoint, {
                    id: item.type === 'pack' ? item._numeric_id : item._uuid,
                    category_id: category.id,
                })
            } catch (error) {
                this.$toast.error(error.response.data.message)
                // Rollback on failure
                item.category_id = previousCategoryId
                item.category_name = previousCategoryName
                item.category_color = previousCategoryColor
                console.error('Error updating category:', error)
                this.$toast?.error(this.$t('food.messages.category_update_error') || 'Erreur lors du changement de catégorie')
            }
        },
        async openCreateCategory() {
            this.activeCategoryDropdown = null
            this.categoryForm = {
                name: '',
                parent_id: null,
                slug: '',
                tags: [],
                color: '#FFD7D7',
                active: true,
                image: null,
            }
            // Fetch tags if not loaded
            if (this.tagsOptions.length === 0) {
                this.fetchTags()
            }
            // Refresh categories for parent selection
            await this.fetchCategories()
            this.isCreateCategoryModalOpen = true
        },
        async saveCategory() {
            if (!this.categoryForm.name) {
                alert(this.$t('food.messages.name_required'))
                return
            }
            this.isSavingCategory = true
            try {
                const formData = new FormData()
                formData.append('name', this.categoryForm.name)
                if (this.categoryForm.parent_id) formData.append('parent_id', this.categoryForm.parent_id)
                if (this.categoryForm.slug) formData.append('slug', this.categoryForm.slug)
                formData.append(
                    'color',
                    this.categoryForm.color.startsWith('#') ? this.categoryForm.color : '#' + this.categoryForm.color
                )
                formData.append('active', this.categoryForm.active ? 1 : 0)
                ;(this.categoryForm.tags || []).forEach((tag) => formData.append('tags[]', tag))

                if (this.categoryForm.image instanceof File) {
                    formData.append('image', this.categoryForm.image)
                } else if (this.categoryForm.image === null) {
                    formData.append('remove_image', '1')
                }

                await axios.post('/back-office/v3/food/categories/store', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                })

                this.isCreateCategoryModalOpen = false
                this.fetchCategories() // Refresh dropdown list
                this.$emit('refresh-stats')
            } catch (e) {
                console.error(e)
                alert(this.$t('food.messages.create_error'))
            } finally {
                this.isSavingCategory = false
            }
        },
        async fetchTags() {
            try {
                const res = await fetch('/back-office/v3/food/categories/tags-list')
                const json = await res.json()
                this.tagsOptions = (json.data || []).map((tag) => ({ label: tag }))
            } catch (e) {
                console.error(e)
            }
        },
        async openBulkCategoryModal() {
            if (this.selectedItems.length === 0) return
            this.bulkCategoryId = null
            // Ensure categories are loaded
            if (this.categories.length === 0) {
                await this.fetchCategories()
            }
            this.showBulkCategoryModal = true
        },
        async openBulkTagModal() {
            if (this.selectedItems.length === 0) return
            this.bulkTagMode = 'add'
            this.bulkTags = []
            // Ensure tags are loaded
            if (this.tagsOptions.length === 0) {
                await this.fetchTags()
            }
            this.showBulkTagModal = true
        },
        openBulkDiscountModal() {
            if (this.selectedItems.length === 0) return
            this.bulkDiscountType = 'percentage'
            this.bulkDiscountValue = null
            this.showBulkDiscountModal = true
        },
        async applyBulkCategory() {
            try {
                await axios.post('/back-office/v3/food/bulk-action', {
                    ids: this.selectedItems,
                    action: 'update_category',
                    data: { category_id: this.bulkCategoryId },
                })
                this.showBulkCategoryModal = false
                this.selectedItems = []
                this.fetchData()
                this.$emit('refresh-stats')
            } catch (e) {
                console.error(e)
                alert(this.$t('food.messages.save_error'))
            }
        },
        async applyBulkTags() {
            await this.handleBulkAction('update_tags')
        },
        async applyBulkDiscount() {
            await this.handleBulkAction('apply_discount')
        },
        async handleBulkAction(action) {
            if (!this.selectedItems.length) return

            const items = this.selectedItems.map((id) => this.items.find((i) => i.id === id)).filter(Boolean)
            const foods = items
                .filter((i) => i.type === 'food')
                .map((i) => i._uuid)
                .filter(Boolean)
            const packs = items.filter((i) => i.type === 'pack').map((i) => i._numeric_id)

            const performAction = async (endpoint, ids, actionData = {}) => {
                if (!ids.length) return
                await axios.post(endpoint, {
                    ids: ids,
                    action: action,
                    data: actionData,
                })
            }

            try {
                if (action === 'delete') {
                    this.showDeletePopup = true
                    this.deleteAction = 'bulk'
                    this.deletePopupDescription = this.$t('food.carte.delete_bulk_confirmation', {
                        count: this.selectedItems.length,
                    })
                    return
                }

                this.isActionLoading = true
                const requests = []

                if (action === 'update_category') {
                    if (this.bulkCategoryId) {
                        requests.push(
                            performAction('/back-office/v3/food/bulk-action', foods, { category_id: this.bulkCategoryId })
                        )
                        requests.push(
                            performAction('/back-office/v3/food/packs/bulk-action', packs, { category_id: this.bulkCategoryId })
                        )
                    }
                    this.showBulkCategoryModal = false
                } else if (action === 'update_tags') {
                    const tagsData = {
                        tags: this.bulkTags.map((t) => (typeof t === 'object' && t !== null ? t.label : t)),
                        mode: this.bulkTagMode,
                    }
                    requests.push(performAction('/back-office/v3/food/bulk-action', foods, tagsData))
                    requests.push(performAction('/back-office/v3/food/packs/bulk-action', packs, tagsData))
                    this.showBulkTagModal = false
                } else if (action === 'apply_discount') {
                    const discountData = {
                        discount_type: this.bulkDiscountType,
                        discount_value: parseFloat(this.bulkDiscountValue),
                    }
                    requests.push(performAction('/back-office/v3/food/bulk-action', foods, discountData))
                    requests.push(performAction('/back-office/v3/food/packs/bulk-action', packs, discountData))
                    this.showBulkDiscountModal = false
                } else {
                    requests.push(performAction('/back-office/v3/food/bulk-action', foods))
                    requests.push(performAction('/back-office/v3/food/packs/bulk-action', packs))
                }

                await Promise.all(requests)

                this.$toast?.success(this.$t('food.carte.bulk_action_success'))
                this.selectedItems = []
                this.fetchData()
                this.$emit('refresh-stats')
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error(error)
                this.$toast?.error(this.$t('food.carte.bulk_action_error'))
            } finally {
                this.isActionLoading = false
            }
        },
        deleteSingleItem(itemId) {
            this.deleteAction = 'single'
            this.itemToDelete = itemId
            const typeLabel = itemId.type === 'pack' ? 'cette formule' : 'ce produit'
            this.deletePopupDescription = `Êtes-vous sûr de vouloir supprimer ${typeLabel} ?`
            this.showDeletePopup = true
        },
        deleteSelectedItems() {
            if (this.selectedItems.length === 0) return
            this.deleteAction = 'multiple'
            this.deletePopupDescription = this.$t('food.messages.confirm_delete_products', { count: this.selectedItems.length })
            this.showDeletePopup = true
        },
        async confirmDelete() {
            if (this.deleteAction === 'single') {
                try {
                    const endpoint =
                        this.itemToDelete.type === 'pack' ? '/back-office/v3/food/packs/delete' : '/back-office/v3/food/delete'
                    const isPack = this.itemToDelete.type === 'pack'
                    await axios.delete(endpoint, {
                        data: {
                            ids: [isPack ? this.itemToDelete._numeric_id : this.itemToDelete._uuid],
                        },
                    })
                    this.fetchData()
                    this.selectedItems = []
                    this.showDeletePopup = false
                    this.$emit('refresh-stats')
                } catch (error) {
                    this.$toast.error(error.response.data.message)
                    console.error('Delete error:', error)
                    alert(this.$t('food.messages.delete_error'))
                }
            } else {
                try {
                    const foods = this.selectedItems
                        .map((id) => this.items.find((i) => i.id === id))
                        .filter((i) => i && i.type === 'food')
                        .map((i) => i._uuid)
                    const packs = this.selectedItems
                        .map((id) => this.items.find((i) => i.id === id))
                        .filter((i) => i && i.type === 'pack')
                        .map((i) => i._numeric_id)

                    if (foods.length) await axios.delete('/back-office/v3/food/delete', { data: { ids: foods } })
                    if (packs.length) await axios.delete('/back-office/v3/food/packs/delete', { data: { ids: packs } })

                    this.fetchData()
                    this.selectedItems = []
                    this.showDeletePopup = false
                    this.$emit('refresh-stats')
                } catch (error) {
                    this.$toast.error(error.response.data.message)
                    console.error('Delete error:', error)
                    alert(this.$t('food.messages.delete_error'))
                }
            }
        },
        openEditTab(item) {
            const id = item.type === 'pack' ? item._numeric_id : item._uuid
            const type = item.type === 'pack' ? 'pack' : 'food'
            window.location.href = `/back-office/v3/food/edit/${id}/${type}`
        },
        openCreateTab() {
            window.location.href = '/back-office/v3/food/create/food'
        },
        openCreatePackTab() {
            window.location.href = '/back-office/v3/food/create/pack'
        },
        duplicateItem(item) {
            this.itemToDuplicate = item
            const typeLabel = item.type === 'pack' ? 'cette formule' : 'ce produit'
            this.duplicatePopupDescription = `Êtes-vous sûr de vouloir dupliquer ${typeLabel} ?`
            this.showDuplicatePopup = true
        },
        async confirmDuplicate() {
            if (!this.itemToDuplicate) return

            try {
                const isPack = this.itemToDuplicate.type === 'pack'
                const endpoint = isPack ? '/back-office/v3/food/packs/duplicate' : '/back-office/v3/food/duplicate'

                const response = await axios.post(endpoint, {
                    id: isPack ? this.itemToDuplicate._numeric_id : this.itemToDuplicate._uuid,
                })
                if (response.data.success) {
                    const newItem = response.data.data
                    const newId = isPack ? newItem.id : newItem.uuid
                    const typeLabel = isPack ? 'Formule' : 'Produit'
                    this.$toast?.success(`${typeLabel} dupliqué avec succès`)
                    window.location.href = `/back-office/v3/food/edit/${newId}/${isPack ? 'pack' : 'food'}`
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Error duplicating item:', error)
                alert('Erreur lors de la duplication')
            } finally {
                this.showDuplicatePopup = false
                this.itemToDuplicate = null
            }
        },
        handleSort(key) {
            if (this.sortBy === key) {
                this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc'
            } else {
                this.sortBy = key
                this.sortOrder = 'asc'
            }
            this.fetchData()
        },
        async openFilterModal() {
            if (this.filterData.categories.length === 0) {
                try {
                    const response = await axios.get('/back-office/v3/food/get-filters-data')
                    this.filterData = response.data.data
                } catch (error) {
                    this.$toast.error(error.response.data.message)
                    console.error('Error fetching filter data:', error)
                }
            }
            this.showFilterModal = true
        },
        handleAdvancedFilters(filters) {
            this.advancedFilters = filters
            this.currentPage = 1
            this.fetchData()
        },
        async fetchData() {
            try {
                const params = {
                    type: this.selectedType,
                    search: this.searchQuery,
                    currentPage: this.currentPage,
                    itemsPerPage: this.itemsPerPage,
                    sortBy: this.sortBy,
                    sortOrder: this.sortOrder,
                    ...this.advancedFilters,
                }

                // Add category slug filter if present from breadcrumb
                if (this.categoryFilter && this.categoryFilter.slug) {
                    params['category_slugs'] = [this.categoryFilter.slug]
                }

                const response = await axios.get('/back-office/v3/food/list-combined', { params })
                this.items = (response.data.data || []).map((item) => ({
                    ...item,
                    _uuid: item.uuid, // raw uuid from backend (foods have this, packs may too)
                    _numeric_id: item.id, // raw numeric id from backend
                    original_id: item.id, // keep for backward compat
                    id: item.type === 'food' ? item.uuid || `food-${item.id}` : `pack-${item.id}`, // unique key
                }))
                this.totalPages = response.data.last_page || 1
            } catch (err) {
                console.error('API error:', err)
            }
        },
        selectType(type) {
            this.selectedType = type
            this.currentPage = 1
            this.fetchData()
        },
        clearFilter() {
            this.$emit('clear-filter')
        },
        async fetchCategories() {
            try {
                const res = await fetch('/back-office/v3/food/categories/fast-list')
                const json = await res.json()
                this.categories = json.data || []
            } catch (err) {
                console.error('Categories error:', err)
            }
        },
        handleClickOutside(event) {
            if (this.activeCategoryDropdown !== null) {
                const btnRef = this.$refs['categoryDropdown-' + this.activeCategoryDropdown]
                const menuRef = this.$refs['categoryMenu-' + this.activeCategoryDropdown]

                const btnEl = Array.isArray(btnRef) ? btnRef[0] : btnRef
                const menuEl = Array.isArray(menuRef) ? menuRef[0] : menuRef

                if (btnEl && !btnEl.contains(event.target) && menuEl && !menuEl.contains(event.target)) {
                    this.activeCategoryDropdown = null
                }
            }
            if (this.showAddDropdown && this.$refs.addDropdown && !this.$refs.addDropdown.contains(event.target)) {
                this.showAddDropdown = false
            }
        },
        async exportData(format = 'csv') {
            if (this.isExporting) return

            const params = {
                format,
                type: this.selectedType,
                search: this.searchQuery,
                ...this.advancedFilters,
            }

            if (this.categoryFilter && this.categoryFilter.slug) {
                params['category_slugs'] = [this.categoryFilter.slug]
            }

            this.isExporting = true
            try {
                const response = await axios.get('/back-office/v3/food/export', {
                    params,
                    responseType: 'blob',
                })

                const url = window.URL.createObjectURL(new Blob([response.data]))
                const link = document.createElement('a')
                link.href = url

                const contentDisposition = response.headers['content-disposition'] || response.headers['Content-Disposition']
                let fileName = `products_export_${format}.${format === 'pdf' ? 'pdf' : format === 'excel' ? 'xlsx' : 'csv'}`
                if (contentDisposition) {
                    const fileNameMatch = contentDisposition.match(/filename=(?:"([^"]+)"|([^;]+))/)
                    if (fileNameMatch) {
                        fileName = fileNameMatch[1] || fileNameMatch[2]
                    }
                }

                link.setAttribute('download', fileName)
                document.body.appendChild(link)
                link.click()

                document.body.removeChild(link)
                window.URL.revokeObjectURL(url)
            } catch (e) {
                console.error(e)
                alert("Une erreur est survenue lors de l'exportation.")
            } finally {
                this.isExporting = false
            }
        },
        async handleDragEnd(event) {
            const { oldIndex, newIndex } = event

            if (oldIndex === newIndex) return

            if (this.isSavingPositions) {
                this.$toast?.info?.(
                    this.$t('food.carte.savingPositions') || 'Veuillez attendre la fin de la sauvegarde en cours...'
                )
                return
            }

            this.isSavingPositions = true

            try {
                const paginationOffset = (this.currentPage - 1) * this.itemsPerPage
                const filteredList = [...this.items]

                const [movedItem] = filteredList.splice(oldIndex, 1)
                filteredList.splice(newIndex, 0, movedItem)

                const computePositions = (list) => {
                    const sortedOrders = list
                        .map((item) => item.order)
                        .sort((a, b) => {
                            return (a ?? 999999) - (b ?? 999999)
                        })
                    return list.map((item, index) => ({
                        id: item._numeric_id,
                        type: item.type,
                        order: sortedOrders[index] ?? paginationOffset + index + 1,
                    }))
                }

                if (this.selectedType === 'tout') {
                    // foods.order et food_packs.order sont deux séquences indépendantes :
                    // redistribuer le multiset de la page mélange les deux espaces de valeurs
                    // et crée des doublons dans une même table (deux lignes au même `order`
                    // => comparateur indéterminé => le glissé ne tient pas). Seul le serveur
                    // voit tout le catalogue, donc lui seul peut produire un rang global.
                    // On lui envoie un voisin plutôt qu'un index : l'index visible dépend de
                    // la pagination et des filtres actifs.
                    const previous = filteredList[newIndex - 1]
                    const next = filteredList[newIndex + 1]
                    const anchor = previous || next

                    await axios.put('/back-office/v3/food/reorder-combined', {
                        moved: { id: movedItem._numeric_id, type: movedItem.type },
                        anchor: { id: anchor._numeric_id, type: anchor.type },
                        placement: previous ? 'after' : 'before',
                    })
                } else {
                    // Un seul type affiché : recycler les ordres déjà présents suffit, ils
                    // appartiennent tous à la même séquence.
                    await this.savePositions(computePositions(filteredList))
                }

                await this.fetchData()

                this.tableKey++

                this.$toast?.success?.(this.$t('food.carte.positions_updated') || 'Ordre mis à jour avec succès')
            } catch (error) {
                this.$toast?.error?.(error.response?.data?.message || 'Erreur lors de la mise à jour')
                await this.fetchData()
            } finally {
                this.isSavingPositions = false
            }
        },
        async savePositions(positions) {
            const foods = positions.filter((p) => p.type === 'food' || !p.type)
            const packs = positions.filter((p) => p.type === 'pack')

            try {
                // Sequential: two independent tables, no cross-entity transaction.
                // Awaiting in order keeps the partial-application window deterministic
                // so a failure always leaves the foods (first write) applied and the
                // caller re-fetches.
                if (foods.length) {
                    await axios.put('/back-office/v3/food/update-positions', { positions: foods })
                }
                if (packs.length) {
                    await axios.put('/back-office/v3/food/packs/update-positions', { positions: packs })
                }
            } catch (error) {
                console.error(error)
                throw error
            }
        },
        getEditUrl(item) {
            const id = item.type === 'pack' ? item._numeric_id : item._uuid
            const type = item.type === 'pack' ? 'pack' : 'food'
            return `/back-office/v3/food/edit/${id}/${type}`
        },
    },
    mounted() {
        this.fetchData()
        this.fetchCategories()
        document.addEventListener('click', this.handleClickOutside)
        window.addEventListener('scroll', this.updateCategoryDropdownPosition, true)
        window.addEventListener('resize', this.updateCategoryDropdownPosition)
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleClickOutside)
        window.removeEventListener('scroll', this.updateCategoryDropdownPosition, true)
        window.removeEventListener('resize', this.updateCategoryDropdownPosition)
    },
}
</script>

<style scoped lang="scss">
.textGray {
    color: #1a1f37;
}

.dark {
    .textGray {
        color: #bfbfbf;
    }
}
</style>
